/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import lodashMergeWith from 'lodash.mergewith';
import type { Store } from '@reduxjs/toolkit';

import Utils from '../../utils/utils';
import Constants from '../../constants';
import CascadeManager from '../../cascade/manager';
import { ActionManagerInstance } from '../../actions/manager';
import MessageManager from '../../messages';
import Validator from '../../validation/validator';
import ContextTreeManager from '../../contextree/treemanager';
import ContextProcessor from '../../context/context-processor';
import type { NodeProps } from '../../contextree/types';
import type { NestedObject } from '../../types';

import * as ActionTypes from './actions';

export type State = {
  data: any;
  containers: {
    accessedOrder?: string[];
    items?: { [key: string]: any };
    type?: string;
    [key: string]: any;
  };
};

let store: Store<State, any, { [key: string]: any }> | null = null;
export const getReferenceTokens = (string: string) => string?.split(/\.(?![^[]+\])|\[|\]/g).filter(Boolean);

/**
 * this function sets storeIn to store object
 * @ignore
 * @param storeIn -- store object
 */
export const setStore = (storeIn: Store<State, any, any> | null) => {
  store = storeIn;
};

/**
 * this function returns store object
 * @ignore
 * @returns - store
 */
export const getStore = () => {
  return store;
};

export type Action = {
  type: string;
  payload: { [key: string]: any };
};

/**
 * this function is wrapper around redux dispatch function and dispatches the action object over store
 * @ignore
 * @param action -- action object
 */
export const dispatch = (action?: Action | Action[]) => {
  if (!action) return;
  let changedListProperty: string[] = [];
  let leafProperty: string[] = [];
  let changedPropertyWithError: string[] = [];

  if (!Array.isArray(action) && action.type === 'MERGE_DATA') {
    ({ changedListProperty, leafProperty, changedPropertyWithError } = CascadeManager.getServerProperties(
      action.payload.data,
      action.payload.context,
      action.payload.rootPageReference
    ));
  }
  store?.dispatch(action);
  if (!Array.isArray(action) && action.type === 'MERGE_DATA') {
    if (changedPropertyWithError.length && !action.payload.skipValidation) {
      const errorsToClear = changedPropertyWithError.map((fieldPath) => {
        const lastIndexOfDot = fieldPath.lastIndexOf('.');
        return {
          property: fieldPath.substring(lastIndexOfDot),
          pageReference: fieldPath.substring(0, lastIndexOfDot)
        };
      });

      errorsToClear.forEach(({ property, pageReference }) => {
        MessageManager.clearMessages({
          type: 'error',
          property,
          pageReference,
          context: action.payload.context
        });

        const fieldNode = ContextTreeManager.getFieldReferenceForProperty(
          action.payload.context,
          `${pageReference}${property}`
        );
        if (fieldNode) {
          const resolvedLiteProps: NodeProps = ContextProcessor.resolveContextTreeNodeProps(fieldNode.props);
          Validator.validate(
            resolvedLiteProps.componentName?.toLowerCase(),
            resolvedLiteProps.propertyName,
            ContextProcessor.getLitePropertyValue(resolvedLiteProps, resolvedLiteProps.propertyName),
            resolvedLiteProps
          );
        }
      });
    }

    CascadeManager.handleServerChanges(action.payload.context, changedListProperty, leafProperty);
  }
};

/**
 * this function will help to provide context data for the given context
 * @ignore
 * @param context -- context name
 * @returns - State Object for the given Context
 */
export const getDataObject = (context?: string | null) => {
  return getStore()?.getState().data?.[context as string];
};

/**
 * This function returns the shared (readonly) data pages from store
 * @public
 * @function getSharedDataObject
 * @ignore
 * @returns shared data object or undefined
 * @example <caption> Example for getSharedDataObject() </caption>
 * const sharedData = pConn.getSharedDataObject();
 */
export const getSharedDataObject = (): { [key: string]: any } | undefined => {
  return getStore()?.getState().data?.[Constants.SHARED];
};

/**
 * this function will help to check wether given string is property reference or not
 *
 * @param str -- Property Name
 * @returns returns -- true if str is property otherwise
 *                  -- false
 */
const isProperty = (str?: string) => {
  return !!(str && !str.startsWith('"') && !str.startsWith("'"));
};

/**
 * this function will help to trim quotes for given string
 *
 * @param str -- Property Name
 * @returns returns -- trimmed str value
 */
const trimQuotes = (str?: string) => {
  return str && str.trim().substring(1, str.length - 1);
};

/**
 * this function will help to get value of the property from state
 *
 * @param propReference -- property reference e.g. .FirstName or pyWorkPage.FirstName
 * @param pageReference -- page reference e.g. .pyWorkPage, D_Accounts.pxResults(1)
 * @param state -- state object
 * @returns - value of the prop
 */
const getValueInternal = (propReference: string | undefined, pageReference: string | null | undefined, state: any) => {
  if (!isProperty(propReference)) {
    return trimQuotes(propReference);
  }
  const fullReference = propReference?.startsWith('.') ? pageReference + propReference : propReference;
  const refTokens = getReferenceTokens(fullReference as string);
  return Utils.getIn(state, refTokens);
};

/**
 * this function will help to get value of the prop from state
 * @ignore
 * @param propReference property reference e.g. .FirstName or pyWorkPage.FirstName
 * @param pageReference page reference e.g. .pyWorkPage, D_Accounts.pxResults(1)
 * @param context context of the property
 * @returns - value of the prop
 */
export const getValue = (propReference?: string, pageReference?: string | null, context?: string | null) => {
  return getValueInternal(propReference, pageReference, getDataObject(context));
};

export const getContextValue = (path: string | null | undefined, prop: string, context?: string | null) => {
  if (path) {
    return getValueInternal(prop, `${Constants.CONTEXT_DATA}.${path}`, getDataObject(context));
  }
  return getValueInternal(prop, `${Constants.CONTEXT_DATA}`, getDataObject(context));
};

const lookupValue = (state: any, pageReference: string, prop: any) => {
  const refTokens = getReferenceTokens(`${pageReference}.${prop}`);
  return Utils.getIn(state, refTokens);
};

export const lookupContextValue = (path: string, prop: string | undefined, context?: string | null) => {
  return lookupValue(getDataObject(context), `${Constants.CONTEXT_DATA}.${path}`, prop);
};

export const getActionType = (options: { context?: any; action?: any }) => {
  const { context } = options;
  const contextObj = getStore()?.getState().data[context];

  if (contextObj) return ActionTypes.UPDATE_DOCUMENT;

  const { action } = options;

  if (['createWork', 'showHarness', 'openAssignment', 'openWorkByHandle', 'initRouting'].includes(action)) {
    return ActionTypes.ADD_CONTAINER_ITEM;
  }

  return ActionTypes.UPDATE_DOCUMENT;
};

/**
 * this function helps to remove ref path in state object
 * @ignore
 * @param state -- State object
 * @param fullReference -- full reference of nested structure in state object
 * @returns - updated state object
 */
export const removeItem = (state: { pyWorkPage: { a: string; b: string } }, fullReference: string) => {
  const refTokens = getReferenceTokens(fullReference);
  return Utils.deleteIn(state, refTokens);
};

export const isMergeable = (obj: any) => {
  return !Array.isArray(obj) && typeof obj === 'object';
};

export const performReplaceAndRearrange = (
  originalData: object,
  propertiesToReplace: {
    path: string;
    allowMultiple: boolean;
  }[] = [],
  propertiesToReArrange: string[] = []
) => {
  if (Array.isArray(propertiesToReplace) && propertiesToReplace.length > 0) {
    propertiesToReplace.forEach((val) => {
      if (!val.allowMultiple) {
        Utils.setIn(originalData, val.path, {});
      } else if (val.allowMultiple && !propertiesToReArrange.includes(val.path)) {
        // Replace multiple attachments when those are not part of rearrange properties
        Utils.setIn(originalData, val.path, []);
      } else {
        // Do nothing
      }
    });
  }
  if (propertiesToReArrange.length > 0) {
    Utils.rearrangeProperties(propertiesToReArrange, originalData as unknown as NestedObject);
  }
};

export const mergeDeep = (
  originalData: any[],
  newObjectData: string | object,
  mergeOptions: {
    isArrayDeepMerge?: boolean;
    propertiesToReplace?: {
      path: string;
      allowMultiple: boolean;
    }[];
    propertiesToReArrange?: string[];
  } = {}
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const { isArrayDeepMerge, propertiesToReplace = [], propertiesToReArrange = [] } = mergeOptions;
  if (Array.isArray(originalData) && !newObjectData) {
    // if originalData is of type Array newObject type might be of type Array but because of null
    // it returns null.
    newObjectData = [];
  }
  performReplaceAndRearrange(originalData, propertiesToReplace, propertiesToReArrange);
  const mergeDeepClosure = (original: string | any[], newObject: string | any[] | null | object): any => {
    if (
      isMergeable(original) &&
      newObject !== null &&
      typeof newObject !== 'string' &&
      typeof newObject !== 'number' &&
      typeof newObject !== 'boolean'
    ) {
      return lodashMergeWith(original, newObject, mergeDeepClosure);
    }

    if (Array.isArray(original) && newObject !== null && Array.isArray(newObject)) {
      if (!isArrayDeepMerge) {
        const newLength = newObject.length;
        if (typeof isArrayDeepMerge === 'undefined' && newLength === original.length) {
          return lodashMergeWith(original, newObject, mergeDeepClosure);
        }
      } else {
        return lodashMergeWith(original, newObject, mergeDeepClosure);
      }
    }
    if (newObject === '' && original && typeof original === 'object') {
      return original;
    }
    return newObject;
  };
  return mergeDeepClosure(originalData, newObjectData);
};

/**
 * this function helps to update value in state object that has sent in corresponding reference
 * @ignore
 * @param state -- State object
 * @param fullReference -- full reference of nested structure in state object
 * @param value -- value to update in reference path
 * @param options - javascript object containing optional parameters like isArrayDeepMerge etc.
 * @param options.isArrayDeepMerge - flag to decide whether to deep merge the objects or values inside array or not
 * @returns - updated state object
 */
export const updateValue = (
  state: any,
  fullReference: string,
  value: any,
  options: { isArrayDeepMerge: boolean } = { isArrayDeepMerge: true }
): any => {
  const { isArrayDeepMerge } = options;
  const refTokens = getReferenceTokens(fullReference);
  if (typeof value === 'object') {
    const draft = mergeDeep(Utils.getIn(state, refTokens), value, {
      isArrayDeepMerge
    });
    return Utils.setIn(state, refTokens, draft);
  }
  return Utils.setIn(state, refTokens, value);
};

/**
 * this function helps to replace value in state object that has sent in corresponding reference
 * @ignore
 * @param state -- State object
 * @param fullReference -- full reference of nested structure in state object
 * @param value -- value to update in reference path
 * @returns - updated state object
 */
export const replaceValue = (state: any, fullReference: string, value: any): any => {
  const refTokens = getReferenceTokens(fullReference);
  return Utils.setIn(state, refTokens, value);
};

/**
 * Reset the Store to its initial state. <strong>Note: This is typically only
 * used by Mashups when they are exiting out of their flow and want
 * to leave the Store in a state that is initialized for the next use.
 * On the initial loading of the app, the store is automatically
 * initialized.
 * @function
 * @private
 * @returns object with actionMgrID and promise keys: { actionMgrID: <theID>, promise: <thePromise> }
 *
 * @example <caption>Example for resetStore()</caption>
 * const store = PCore.resetStore();
 */
export const resetStore = () => {
  const theType = ActionTypes.RESET_STORE;

  // register this action with the ActionManager.
  //  Note that actionsApiPromise creates the ActionManagerID (since
  //  we're not passing in an object that already has an ID)
  //  and puts it in the returned promise
  const { promise, actionMgrID } = ActionManagerInstance.actionManagerPromise(theType);

  dispatch({
    type: theType,
    payload: { actionMgrID }
  });

  // return the Promise associated with this action
  return promise;
};

/**
 * this function helps to check whether the context exists in store or not
 * @ignore
 * @param context -- context name
 * @returns - true if context exists otherwise false
 */
export const doesContextExist = (context: string) => {
  const state = getStore()?.getState();
  return !!state?.data?.[context];
};
